import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import CmTable from '@/components/cmTable/index.vue'
import SearchFilterBar from '@/components/searchFilterBar/index.vue'
import { reactive, ref, computed } from 'vue'
import { useCmTableListRefresh } from '@/hooks/use-cm-table-list-refresh'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import {
  ActionType,
  ActionTypeValuesByKeys,
  DateType,
  FormActionType,
  FormSize,
  FormSizeType,
  FormType,
  ObjectFormItem,
} from '@/components/cmForm/types'
import { PolicyCommonService } from '@/views/dashboard/policy/policy-common.service'
import { TerminalType } from '@/views/dashboard/_enum/terminal-type'
import { computerInfoCols } from '../data-safe/ransomware-protection/types/ransomware-incident'
import { useParseTime } from '@/hooks'
import { formatTimeStamp } from '@/utils/time'
import { useRoute } from 'vue-router'
import dayjs from 'dayjs'
import { TableOpt, TableActOpt } from '@/components/cmTable/types'
import {  AuthLogItem, RemoteAuthLoginService, RequestParams, } from './service'
import GroupTreeSelector from '../_components/group-tree-selector/group-tree-selector.vue'
import { LogType } from './types'
import RawDataDrawer from '@/views/dashboard/_components/raw-data-drawer/raw-data-drawer.vue'
import { useI18n } from 'vue-i18n'
import { listCol } from '@/utils/list-columns-i18n'

type ExcludeRequestParams = Exclude<keyof RequestParams, 'limit' | 'page' | 'device_id' | 'date'>

export default /*#__PURE__*/_defineComponent({
  __name: 'remote-login-log',
  setup(__props) {

const { t, locale } = useI18n()

function logTypeLabel(value: number): string {
  const map: Record<number, string> = {
    1: t('remoteAuth.logTypeSuccess'),
    2: t('remoteAuth.logTypeLogout'),
    3: t('remoteAuth.logTypeFail'),
    4: t('remoteAuth.logTypeAuthSuccess'),
    5: t('remoteAuth.logTypeAuthFail'),
  }
  return map[value] ?? '--'
}

const injector = useReflectiveInjector([RemoteAuthLoginService, PolicyCommonService])
const remoteAuthLoginService = injector.get(RemoteAuthLoginService)
const api = remoteAuthLoginService.getTerminalUserLoginLogPage.bind(remoteAuthLoginService)
const exportSensitiveFileSendApi = remoteAuthLoginService.getTerminalUserLoginLogExport.bind(remoteAuthLoginService)
const tableRef = ref()
const { refreshList } = useCmTableListRefresh(tableRef)
const formType = ref(FormActionType.Other)
const formSize: FormSizeType = FormSize.Small
const defaultDate = [dayjs().subtract(30, 'day').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')]
const [start_time, end_time] = useParseTime(defaultDate)
const queryForm = reactive<Partial<RequestParams> & { dateRange?: any[] }>({
  dateRange: defaultDate,
  start_time,
  end_time,
  event_type: null,
  log_type: null,
  source_ip: null,
  key_word:'',
})
const queryFormColumns = computed<ObjectFormItem<RequestParams & { dateRange?: any[] }, ExcludeRequestParams | 'dateRange'>>(() => {
  void locale.value
  return {
    group_id: {
      colOpt: { style: 'flex: 0 0 130px' },
      slot: true,
      label: t('remoteAuth.group'),
      hiddenAllLabel: true,
      outSearchFiledFlag: true,
      events: {
        change: () => void refreshList(),
      },
    },
    dateRange: {
      colOpt: { style: 'flex: 0 0 280px' },
      label: t('remoteAuth.dateRange'),
      type: FormType.Date,
      dateType: DateType.DateRange,
      events: {
        change: (val) => {
          const [start, end] = useParseTime(val)
          queryForm.start_time = start
          queryForm.end_time = end
          void refreshList()
        },
      },
      dateOpt: {
        'value-format': 'YYYY-MM-DD',
        'start-placeholder': t('remoteAuth.startPlaceholder'),
        'end-placeholder': t('remoteAuth.endPlaceholder'),
        clearable: false,
      },
    },
    log_type: {
      colOpt: { style: 'flex: 0 0 130px' },
      label: t('remoteAuth.logType'),
      type: FormType.Select,
      selectOpt: {
        clearable: true,
      },
      events: {
        change: () => { void refreshList() },
      },
      dictData: [
        { label: t('remoteAuth.logTypeSuccess'), value: LogType.success },
        { label: t('remoteAuth.logTypeLogout'), value: LogType.logout },
        { label: t('remoteAuth.logTypeFail'), value: LogType.fail },
        { label: t('remoteAuth.logTypeAuthSuccess'), value: LogType.auth_success },
        { label: t('remoteAuth.logTypeAuthFail'), value: LogType.auth_fail },
      ]
    },
  }
})

async function handleSearch(value: string) {
  queryForm.key_word = value
  await refreshList()
}

async function handleQuery(form: any) {
  Object.assign(queryForm, form)
  if (form.dateRange) {
    const [start, end] = useParseTime(form.dateRange)
    queryForm.start_time = start
    queryForm.end_time = end
  }
  await refreshList()
}

function handleReset() {
  queryForm.group_id = undefined
  queryForm.dateRange = defaultDate
  const [start, end] = useParseTime(defaultDate)
  queryForm.start_time = start
  queryForm.end_time = end_time
  queryForm.log_type = null
  queryForm.key_word = ''
  void refreshList()
}
const isShowRawData = ref(false)
const currentRow = ref<any>({})

const tableActOpt = computed<TableActOpt>(() => {
  void locale.value
  return {
    columnOpt: { label: listCol(t, 'operation'), width: 120, align: 'left', fixed: 'right' },
    actionButtons: (row: AuthLogItem) => [
      {
        name: listCol(t, 'dataSource'),
        type: 'rawData',
        opt: { size: FormSize.Small, text: true, type: 'text' },
      },
    ],
  }
})

const tableOpt = computed<TableOpt>(() => {
  void locale.value
  return {
    bodyStyle: 'background: var(--cm-color-card-bg);padding:0 16px 16px',
    containerOpt: { style: 'padding: 0;background: var(--cm-color-card-bg)' },
    height: '100%',
    path: 'data',
    showActionBar: false,
    showForm: false,
    showRightToolbar: false,
    rowKey: 'id',
    selection: false,
    columns: [
      {
        label: listCol(t, 'loginTime'),
        prop: 'log_time',
        minWidth: 150,
        formatter: (_row, val) => formatTimeStamp(val, 'YYYY-MM-DD HH:mm')
      },
      ...computerInfoCols(true, true, t),
      {
        label: listCol(t, 'loginSourceIp'),
        prop: 'source_ip',
        minWidth: 130,
      }, {
        label: listCol(t, 'loginProtocol'),
        prop: 'login_protocol',
        minWidth: 90
      }, {
        label: listCol(t, 'loginAccount'),
        prop: 'name',formatter: (_row, value) => _row.domain+'\\'+value,
        minWidth: 230,
      },{
        label: listCol(t, 'loginRole'),
        prop: 'is_admin',
        minWidth: 100,formatter: (_row, value) => value === 1 ? t('remoteAuth.admin') : t('remoteAuth.nonAdmin')
      },{
        label: listCol(t, 'result'),
        prop: 'log_type',formatter: (_row, value) => logTypeLabel(value),
        minWidth: 140
      },

    ],
  }
})

const handleAction = ({
  type,
  rowItem,
}: {
  type: ActionTypeValuesByKeys
  rowItem?: AuthLogItem
  other?: any[]
}) => {
  const types: { [p: string]: () => void } = {
    [ActionType.Download]: () => {
      exportSensitiveFileSendApi(0, queryForm, '远程登录认证日志')
    },
    rawData: () => {
      currentRow.value = rowItem
      isShowRawData.value = true
    },
  }
  types[type]?.()
}
const updateGroupId = async (groupId) => {
  queryForm.group_id = groupId
  await refreshList()
}


return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(CmTable, {
      tableOpt: tableOpt.value,
      modelValue: queryForm,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((queryForm) = $event)),
      tableActOpt: tableActOpt.value,
      "has-permission": true,
      api: _unref(api),
      ref_key: "tableRef",
      ref: tableRef,
      pageOption: {
    style: 'padding-bottom: 16px',
    pageNumName: 'page',
    pageSizeName: 'limit',
  },
      onHandleAction: handleAction,
      class: "cm-ui-shadow-container"
    }, {
      header: _withCtx(({ rowData }) => [
        _createVNode(SearchFilterBar, {
          style: {"padding":"16px 0 0","background-color":"var(--cm-color-card-bg)"},
          modelValue: queryForm,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((queryForm) = $event)),
          "form-columns": queryFormColumns.value,
          "show-search": true,
          "search-placeholder": _ctx.$t('remoteAuth.searchPlaceholder'),
          "popover-threshold": 2,
          onSearch: handleSearch,
          onQuery: handleQuery,
          onReset: handleReset
        }, {
          actions: _withCtx(() => [
            _createVNode(_component_el_button, {
              size: _unref(formSize),
              onClick: _cache[0] || (_cache[0] = ($event: any) => (handleAction({ type: _unref(ActionType).Download }))),
              type: "primary",
              plain: ""
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('remoteAuth.exportRecord')), 1)
              ]),
              _: 1
            }, 8, ["size"]),
            _createVNode(_component_router_link, {
              class: "cm-ui-ml10",
              to: "/dashboard/remote-authentication"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_button, {
                  type: "primary",
                  size: "small",
                  plain: ""
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t('remoteAuth.viewRemoteAuthPolicy')), 1)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          group_id: _withCtx(() => [
            _createVNode(GroupTreeSelector, {
              width: "100%",
              "terminal-type": [_unref(TerminalType).windows, _unref(TerminalType).windowsServer],
              onSelected: updateGroupId
            }, null, 8, ["terminal-type"])
          ]),
          _: 1
        }, 8, ["modelValue", "form-columns", "search-placeholder"])
      ]),
      _: 1
    }, 8, ["tableOpt", "modelValue", "tableActOpt", "api"]),
    _createVNode(RawDataDrawer, {
      modelValue: isShowRawData.value,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((isShowRawData).value = $event)),
      "raw-data": currentRow.value
    }, null, 8, ["modelValue", "raw-data"])
  ], 64))
}
}

})